import { useMemo } from 'react';
import { type FeatureFlags, useFeatureFlag } from '../../../hooks/useFeatureFlag';

export type QuotesV2GroupBy = 'RFQ' | 'Sweepable' | 'CareOrder' | 'InitiatingFrom';

export interface QuotesV2GroupByOption {
  value: QuotesV2GroupBy;
  label: string;
  testID: string;
}

interface QuotesV2GroupByOptionConfig extends QuotesV2GroupByOption {
  isAvailable: (featureFlags: FeatureFlags) => boolean;
  groupHeaderPrefix?: string;
}

const isCareOrderGroupingAvailable = (featureFlags: FeatureFlags) =>
  featureFlags.enrichRFQBlotterV2WithCareOrders && featureFlags.enableTakerMarketplaceETFRFQFlow;

const QUOTES_V2_GROUP_BY_OPTIONS: QuotesV2GroupByOptionConfig[] = [
  {
    value: 'RFQ',
    label: 'RFQ',
    testID: 'group-by-rfq-button',
    isAvailable: () => true,
  },
  {
    value: 'Sweepable',
    label: 'Sweepable',
    testID: 'group-by-sweepable-button',
    isAvailable: featureFlags => featureFlags.enableRFQSweeping,
    groupHeaderPrefix: 'Sweep Group ID',
  },
  {
    value: 'CareOrder',
    label: 'Care Order',
    testID: 'group-by-care-order-button',
    isAvailable: isCareOrderGroupingAvailable,
    groupHeaderPrefix: 'Care Order ID',
  },
  {
    value: 'InitiatingFrom',
    label: 'Initiating From',
    testID: 'group-by-initiating-from-button',
    isAvailable: isCareOrderGroupingAvailable,
    groupHeaderPrefix: 'Initiating From',
  },
];

interface UseQuotesV2GroupByParams {
  groupBy?: QuotesV2GroupBy;
}

export function useQuotesV2GroupBy({ groupBy = 'RFQ' }: UseQuotesV2GroupByParams) {
  const featureFlags = useFeatureFlag();
  const availableOptions = useMemo(
    () => QUOTES_V2_GROUP_BY_OPTIONS.filter(option => option.isAvailable(featureFlags)),
    [featureFlags]
  );

  const selectedOption = useMemo(
    () => availableOptions.find(option => option.value === groupBy) ?? QUOTES_V2_GROUP_BY_OPTIONS[0],
    [availableOptions, groupBy]
  );

  return useMemo(
    () => ({
      availableOptions,
      selectedGroupBy: selectedOption.value,
      groupHeaderPrefix: selectedOption.groupHeaderPrefix,
      rowGroupingOptions: {
        groupByInitiatingFrom: selectedOption.value === 'InitiatingFrom',
        groupByParentRFQID: selectedOption.value === 'CareOrder',
        groupBySweepGroupID: selectedOption.value === 'Sweepable',
      },
    }),
    [availableOptions, selectedOption]
  );
}
